import { useRef } from "react";

/**
 * Shallow-compares two objects by their own enumerable keys and values.
 * Returns `true` when the objects are considered equal.
 */
export function shallowEqual<T extends Record<string, unknown>>(
  a: T,
  b: T,
): boolean {
  const aKeys = Object.keys(a);
  const bKeys = Object.keys(b);
  if (aKeys.length !== bKeys.length) return false;
  return aKeys.every((k) => Object.hasOwn(b, k) && a[k] === b[k]);
}

/**
 * Returns a referentially-stable version of `obj` that only updates when a
 * shallow comparison of its own-property values detects a change. This prevents
 * inline object literals from invalidating downstream memoization.
 */
export function useShallowStable<T extends Record<string, unknown>>(obj: T): T {
  const ref = useRef(obj);

  if (!shallowEqual(ref.current, obj)) {
    ref.current = obj;
  }

  return ref.current;
}
